import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  GetMessageWithIntl,
  Toast,
  error,
  success,
} from '~/shared/components';
import { PageNavigation, Textarea, Textbox } from '~/shared/components/ui';
import { CaptionButton } from '~/shared/components/ui/Button';
import { RadioButton } from '~/shared/components/ui/Radiobutton';
import { useConfirmation } from '~/shared/hooks';
import { useLoading } from '~/shared/contexts/LoadingProvider';
import {
  ViewId,
  formatUserWIthEmail,
  getMaxMainContentsHeight,
  getWorkerExceptionMessage,
  includeInputValidateError,
} from '~/shared/utils';
import {
  FullMethodName_ListBusinessUnitManagements,
  FullMethodName_UncompletedListBusinessUnitRelationRequests,
} from '~/worker';
import { mtechnavi } from '~/shared/libs/clientsdk';
import { getNextBusinessUnitManagementCode } from './utils/code';

const VIEW_ID: ViewId = 'BUM_BUSINESS_UNIT_RELATION_RECEPTION';

type RelationRequest = mtechnavi.api.company.IBusinessUnitRelationRequest;

/** 承諾する／承諾しない */
const ANSWER_ACCEPT = '1';
const ANSWER_REJECT = '0';

export function BusinessUnitRelationReception() {
  const intl = useIntl();
  const navi = useNavigate();
  const { showLoading, hideLoading } = useLoading();
  const { confirmation, confirmationElement } = useConfirmation();

  const [items, setItems] = useState<RelationRequest[]>([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [mainContentHeight, setMainContentHeight] = useState('');
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [answer, setAnswer] = useState('');

  const footerRef = useRef<HTMLDivElement>(null);
  const requiredArea = useRef(null);
  const requiredAnswerArea = useRef(null);
  const nextCode = useRef('');

  const item = items.at(pageNumber);

  // メールURL経由は /{画面ID}/{取引申請ID}、タスクリスト経由は /{画面ID}
  const splitPath = window.location.pathname.split('/');
  const relationRequestId = splitPath.length >= 3 ? splitPath[2] : '';

  const getLabelId = (column: string): string => `${VIEW_ID}.${column}`;

  /** 未処理の取引申請が無い場合はトップページへ遷移する */
  const load = async () => {
    try {
      const [res, managements] = await Promise.all([
        window.App.services.ui.worker.filter({
          action: 'reload',
          fullMethodName:
            FullMethodName_UncompletedListBusinessUnitRelationRequests,
          filter: {},
          sort: [],
          requestBody: relationRequestId
            ? { businessUnitRelationRequestIds: [relationRequestId] }
            : {},
        }) as Promise<mtechnavi.api.company.UncompletedListBusinessUnitRelationRequestsResponse>,
        window.App.services.ui.worker.filter({
          action: 'reload',
          fullMethodName: FullMethodName_ListBusinessUnitManagements,
          filter: {},
          sort: [],
        }) as Promise<mtechnavi.api.company.ListBusinessUnitManagementsResponse>,
      ]);
      nextCode.current = getNextBusinessUnitManagementCode(
        (managements.items ?? []).map((v) => v.code)
      );
      const nextItems = res.items ?? [];
      if (nextItems.length === 0) {
        navi('/');
        return;
      }
      setPageNumber((prev) => Math.min(prev, nextItems.length - 1));
      setItems(nextItems);
    } catch (err) {
      error(getWorkerExceptionMessage(intl, err));
    }
  };

  useEffect(() => {
    (async () => {
      showLoading();
      await load();
      hideLoading();
    })();
    // 初回のみ取得する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setMainContentHeight(
      getMaxMainContentsHeight(footerRef.current?.clientHeight ?? 0)
    );
  }, [footerRef.current?.clientHeight]);

  useEffect(() => {
    setCode(nextCode.current);
    setDisplayName(items.at(pageNumber)?.sourceCompanyDisplayName ?? '');
    setAnswer('');
  }, [items, pageNumber]);

  const answerItems = [
    {
      value: ANSWER_ACCEPT,
      displayName: GetMessageWithIntl(intl, {
        prefixId: VIEW_ID,
        id: 'answer.accept',
      }),
    },
    {
      value: ANSWER_REJECT,
      displayName: GetMessageWithIntl(intl, {
        prefixId: VIEW_ID,
        id: 'answer.reject',
      }),
    },
  ];

  const isInputError = (): boolean => {
    const targetElm = document.querySelector('.BusinessUnitRelationReception');
    return includeInputValidateError(targetElm, intl, [
      { value: code, ref: requiredArea },
      { value: displayName, ref: requiredArea },
      { value: answer, ref: requiredAnswerArea },
    ]);
  };

  const handleRegister = async () => {
    if (isInputError()) {
      return;
    }
    const isAccept = answer === ANSWER_ACCEPT;
    const confirm = confirmation({
      prefixId: VIEW_ID,
      id: 'registerConfirmMessage',
      value: {
        $1: item?.companyDisplayName ?? '',
        $2: displayName,
        $3: code,
        $4: <b>{answerItems.find((v) => v.value === answer)?.displayName}</b>,
      },
    });
    if (!(await confirm)) {
      return;
    }

    showLoading();
    try {
      const businessUnitManagementId =
        item?.businessUnitRelationRequestId ?? '';
      await window.App.services.ui.worker.apiCall(
        isAccept
          ? {
              actionName: 'acceptBusinessUnitRelationReception',
              request: {
                businessUnitManagementId,
                businessUnitManagementCode: code,
                businessUnitManagementDisplayName: displayName,
              },
            }
          : {
              actionName: 'rejectBusinessUnitRelationReception',
              request: { businessUnitManagementId },
            }
      );
      success([GetMessageWithIntl(intl, { id: 'I0000001' })]);
      await load();
    } catch (err) {
      error(getWorkerExceptionMessage(intl, err));
    } finally {
      hideLoading();
    }
  };

  return (
    <Container viewId={VIEW_ID}>
      <div className="BusinessUnitRelationReception">
        <div className="header">
          <PageNavigation
            backpagePath="/"
            pageInfo={{
              isUnvisibleBackNavi: true,
              isVisibleMoveNavi: !relationRequestId,
              isFirstPage: pageNumber === 0,
              isMaxPage: pageNumber === items.length - 1,
              pageNumber: pageNumber + 1,
            }}
            iconItems={[]}
            infoOption={{
              lastUpdateInfo: { isVisibleUpdateInfo: false },
              issuerInfo: { isVisibleIssuerInfo: false },
            }}
            handleMovePage={(n) => setPageNumber(n - 1)}
          />
        </div>
        <div>
          <div
            className="scroll-main-contents-area"
            style={{ maxHeight: mainContentHeight }}
          >
            {item && (
              <>
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-50">
                      <Textbox
                        name="sourceCompanyDisplayName"
                        type="text"
                        labelId={getLabelId('sourceCompanyDisplayName')}
                        columns={['sourceCompanyDisplayName']}
                        value={item.sourceCompanyDisplayName ?? ''}
                        disabled={true}
                      />
                    </div>
                  </div>
                </div>
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-50">
                      <Textbox
                        name="companyDisplayName"
                        type="text"
                        labelId={getLabelId('companyDisplayName')}
                        columns={['companyDisplayName']}
                        value={item.companyDisplayName ?? ''}
                        disabled={true}
                      />
                    </div>
                  </div>
                </div>
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-50">
                      <Textbox
                        name="contact"
                        type="text"
                        labelId={getLabelId('contact')}
                        columns={['contact']}
                        value={formatUserWIthEmail(
                          item.contact?.displayName,
                          item.contact?.email
                        )}
                        disabled={true}
                      />
                    </div>
                    <div className="w-25">
                      <Textbox
                        name="contactDepartment"
                        type="text"
                        labelId={getLabelId('contactDepartment')}
                        columns={['contactDepartment']}
                        value={item.contactDepartment ?? ''}
                        disabled={true}
                      />
                    </div>
                  </div>
                </div>
                <div className="input-line" ref={requiredArea}>
                  <div className="item-group-100">
                    <div className="w-25">
                      <Textbox
                        name="code"
                        type="text"
                        labelId={getLabelId('code')}
                        columns={['code']}
                        value={code}
                        validateOption={{ required: true }}
                        onChangeState={setCode}
                      />
                    </div>
                    <div className="w-50">
                      <Textbox
                        name="displayName"
                        type="text"
                        labelId={getLabelId('displayName')}
                        columns={['displayName']}
                        value={displayName}
                        validateOption={{ required: true }}
                        onChangeState={setDisplayName}
                      />
                    </div>
                  </div>
                </div>
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-75">
                      <Textarea
                        name="terms"
                        labelId={getLabelId('terms')}
                        columns={['terms']}
                        className="w-100 mh-high"
                        value={GetMessageWithIntl(intl, {
                          prefixId: VIEW_ID,
                          id: 'termsValue',
                        })}
                        disabled={true}
                      />
                    </div>
                  </div>
                </div>
                <div className="input-line" ref={requiredAnswerArea}>
                  <div className="item-group-100">
                    <div className="w-50">
                      <RadioButton
                        name="answer"
                        className="group"
                        columns={['answer']}
                        items={answerItems}
                        value={answer}
                        validateOption={{ required: true }}
                        onChangeState={setAnswer}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="footer" ref={footerRef}>
            {item && (
              <div className="footer-contents">
                <div className="input-line">
                  <CaptionButton
                    name="registerBtn"
                    caption={GetMessageWithIntl(intl, { id: 'register' })}
                    className="btn"
                    buttonType="basic"
                    onClick={() => handleRegister()}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {confirmationElement}
      <Toast />
    </Container>
  );
}
